/**
 * String values returned by the API for questions, wagers/bets, positions, and transaction actions.
 * Keep comparisons and payloads aligned with the backend.
 */
export const QUESTION_STATUS = Object.freeze({
  OPEN: 'open',
  STOPPED: 'stopped',
  REVERSED: 'reversed',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  RESOLVED: 'resolved',
})

export const MARKET_POSITION = Object.freeze({
  LONG: 'long',
  SHORT: 'short',
})

/** Wager / bet row `status` (lifecycle). Same vocabulary as `WAGER_ACTION` where they overlap. */
export const WAGER_STATUS = Object.freeze({
  SALE: 'sale',
  PARTIAL_SALE: 'partial_sale',
  PURCHASE: 'purchased',
  WIN: 'won',
  LOSE: 'lost',
  CANCEL: 'cancel',
  REVERSED: 'reversed',
  DISABLE: 'disable',
})

/** Transaction / history row `action`. */
export const WAGER_ACTION = WAGER_STATUS

const ACTION_NORMALIZE_MAP = Object.freeze({
  purchase: WAGER_STATUS.PURCHASE,
  win: WAGER_STATUS.WIN,
  lose: WAGER_STATUS.LOSE,
})

export function normalizeWagerAction(action) {
  return ACTION_NORMALIZE_MAP[action] || action
}

/**
 * Wager `status`/`action` values that represent a full or partial sale. The API has shipped
 * both present-tense (`sale`/`partial_sale`) and past-tense (`sold`/`partial_sold`) spellings
 * for this lifecycle state, so accept every known variant instead of a single literal.
 */
const SALE_LIKE_STATUSES = Object.freeze([
  WAGER_STATUS.SALE,
  WAGER_STATUS.PARTIAL_SALE,
  'sold',
  'partial_sold',
  'partially_sold',
])

export function isWagerSaleLikeAction(action) {
  return SALE_LIKE_STATUSES.includes(normalizeWagerAction(action))
}

export function isWagerSaleLikeStatus(status) {
  return SALE_LIKE_STATUSES.includes(status)
}

/** Maps a wager `status` (raw or legacy variant) to the design system Tag `type`/`inverse` props. */
export function getWagerStatusTag(status) {
  if (status === WAGER_STATUS.PURCHASE || status === 'purchased' || status === 'p') {
    return { type: 'informative-1' }
  }
  if (isWagerSaleLikeStatus(status) || status === 's' || status === 'ps') {
    return { type: 'warning' }
  }
  if (status === WAGER_STATUS.WIN || status === 'w') {
    return { type: 'positive' }
  }
  if (status === WAGER_STATUS.LOSE || status === 'l') {
    return { type: 'negative' }
  }
  if (status === 'paused') {
    return { type: 'informative-2' }
  }
  return { type: 'neutral' }
}

/**
 * Resolve the amount a position was sold for. Newer responses expose it as `amount_on_sell`,
 * while older/historical rows used `last_sell_amount`; prefer the explicit last-sell value when
 * present so historical positions keep their exact proceeds.
 */
export function getWagerSoldAmount(bet) {
  if (!bet) return null
  return bet.last_sell_amount ?? bet.amount_on_sell ?? null
}
